import moment from 'moment'
import {orderBy, chain} from 'lodash'
import update from 'immutability-helper'
import {uuid, isEmpty, htmlToText} from './utils'
import create from 'helpers/command'

const questionTypeHandlers = {
  'multiple-choice': initMultipleChoice,
  'matching': initMatching
}

export function selectCourseLabel({course, courseName}) {
  return `${course.substring(0,4)}_${courseName}`
}

export function initQuestion (context) {
  return (question) => {
    const {quizQuestionTypes: types} = context
    const {name: typeName} = types.find(t => t.id == question.type)
    const handler = questionTypeHandlers[typeName]
    return {modifications: 0, ...(handler ? handler(question) : question)}
  }
}

function initMultipleChoice (question) {
  return question.answer.choices 
    ? update(question, {
        properties: {$apply: properties => ({singleOption: true, ...properties})},
        answer: {
          initialized: {$set: true},
          choices: {$apply: choices =>
            choices.map((choice, id) => ({...choice, id: uuid()}))
          }}})
    : question
}

function initMatching (question) {
  return question.answer.choices
    ? update(question, {
        answer: {
          initialized: {$set: true},
          choices: {$apply: choices =>
            choices.map(choice => [...choice, uuid()])
          }}})
    : question
}

const valueValidation = [({properties}) => !properties.cleared, 'missing-value']

const hasTextOrImage = (text, html) => {
  return !isEmpty(text) || /<img src=/.test(html);
}

const premiseValidation = [(question) => {
  const {premise, textpremise} = question
  return hasTextOrImage(textpremise, premise.html)
}, 'missing-premise']

export const validationHandlers = {
  'true-or-false': [
    valueValidation,
    premiseValidation,
    [({answer}) => answer.value != undefined, 'true-or-false-missing-answer'],
  ],
  'open-question': [
    valueValidation,
    premiseValidation
  ],
  'fill-in-the-blanks': [
    valueValidation,
    premiseValidation,
    [({answer}) => answer.phrases && answer.phrases.findIndex(p => p.blanked) >= 0, 'fill-in-the-blanks-missing-blanks'],
  ],
  'multiple-choice': [
    valueValidation,
    premiseValidation,
    [({answer}) => answer.choices.findIndex(c => c.correct) >= 0, 'multiple-choice-missing-correct-answer'],
    [({answer}) => answer.choices.every(c => hasTextOrImage(htmlToText(c.html), c.html)), ({answer}, context) => {
      return answer.choices
        .map((choice, index) => !hasTextOrImage(htmlToText(choice.html), choice.html) && context.translate('multiple-choice-missing-html', {choice: String.fromCharCode(index + 97)}))
        .filter(c => c)
    }],
  ],
  'matching': [
    valueValidation,
    [({answer}) => {
      return answer.choices.every(([term, definition]) => {
        return hasTextOrImage(htmlToText(term), term) && hasTextOrImage(htmlToText(definition), definition)
      })
    }, ({answer}, context) => {
      return answer.choices
        .map(([term, definition], index) => {

          const missingTerm = !hasTextOrImage(htmlToText(term), term)
          const missingDefinition = !hasTextOrImage(htmlToText(definition), definition)

          if (!missingDefinition && !missingTerm) return false

          const choice = String.fromCharCode(index + 97)

          if (missingDefinition && missingTerm) return context.translate('matching-missing-term-and-definition', {choice})
          if (missingDefinition) return context.translate('matching-missing-definition', {choice})
          if (missingTerm) return context.translate('matching-missing-term', {choice})

        })
        .filter(c => c)
    }],
  ]
}

const assessmentValidations = [
  [({quizQuestions}) => {
    return Object.values(quizQuestions).length > 0
  }, 'assessment-missing-questions']
]

export function isValidQuestion(question, type) {
  const validations = validationHandlers[type] || []
  return validations.every(([validate]) => validate(question))
}

export function validateAssessment(sections, context) {

  const getTypeName = id => {
    const type = context.quizQuestionTypes.find(t => t.id == id)
    return type && type.name
  }
  const sectionIndex = id => context.quizSections.findIndex(s => s.id == id)

  const assessmentErrors = assessmentValidations.filter(([validate]) => !validate(context)).map(([_,message]) => message)

  const questionErrors = orderBy(Object.entries(sections).reduce((sections, [section, questions]) => {

    const targets = questions.filter(q => !isValidQuestion(q, getTypeName(q.type)))

    if (targets.length > 0) {
      sections.push({id: section, index: sectionIndex(section), questions: targets.reduce((questions, question, index) => {
        const typeName = getTypeName(question.type)
        const validations = validationHandlers[typeName] || []
        questions.push({
          ...question,
          index,
          typeName,
          errors: validations.map(([validate, message]) => validate(question) ? false : message).filter(e => e)
        })
        return questions
      }, [])})
    }
    return sections
  }, []), ['index'])
  return {assessmentErrors, questionErrors}
}

const assessmentStatus = [
  {
    status: 'started',
    test: ({duration, finished, durationSinceStart}) => !finished && durationSinceStart > 0 && duration > durationSinceStart
  },
  {
    status: 'expired',
    test: ({finished, duration, durationSinceStart}) => !finished && durationSinceStart >= duration
  },
  {
    status: 'finished',
    test: ({started, finished}) => started != null && finished != null
  },
  {
    status: 'missed',
    test: ({finish, started, finished}) => moment().isAfter(finish) && !started && !finished
  },
  {
    status: 'available',
    test: ({started, start, finish}) => !started && moment().isBetween(start, finish)
  },
  {
    status: 'unavailable',
    test: ({started, start, finish}) => !started && moment().isBefore(start)
  }
]

export function getAssessmentStatus (assessment) {
  const {status} = assessmentStatus.find(a => a.test(assessment)) || {status: 'missed'}
  return status
}

export function getAssessmentPoints (studentAssessment) {
  const {results, properties = {}} = studentAssessment || {}
  const {bonus = 0, published = false} = properties
  const points = Object.values(results || {}).reduce((acc, result) => {
    return {points: acc.points + result.points, total: acc.total + result.total, pending: acc.pending + (result.pending ? 1 : 0)}
  }, {points: bonus || 0, total: 0, pending: 0})
  return {published, ...points} 
}

export function createValidateStatus(context) {
  return async ({id}) => {
    const [paging, results] = await create('quiz', 'searchAssignments')({id})
    if (paging.recordCount < 1) return false
    const [assignment] = results
    const {startedCount, assessmentProperties: {validated}} = assignment
    if (startedCount > 0) {
      const index = context.quizAssignments.findIndex(a => a.id == assignment.id)
      if (index >= 0)
        await context.updateState({quizAssignments: {[index]: {$set: assignment}}})
      return false
    }
    return true
  }
}

export function getQuizQuestionStandards({quizQuestions, quizSections}) {
  return chain(Object.values(quizQuestions))
    .flatten()
    .orderBy(['section', 'index'])
    .map(question => {
      const {id, properties, index} = question
      const section = quizSections.find(s => s.id == question.section) || {}
      return {id, standards: properties.standards || [], index, sectionIndex: section.index}
    })
    .value()
}

export function standardFilters(filters) {
  return Object.entries(filters).filter(([k, v]) => v.length > 0).reduce((filters, [k, v]) => ({...filters, [k]: v}), {})
}
